import type { Id, Tournament } from './types.js';
import { effectiveRules } from './rules/presets.js';

export interface Eta {
  remainingMatches: number;
  runningMatches: number;
  activeCourts: number;
  avgMatchMinutes: number;
  /** Voraussichtliches Ende (ms) oder undefined, wenn kein Feld aktiv */
  endsAt?: number;
  /** ± Unsicherheit in Minuten */
  spreadMinutes: number;
}

/**
 * Zeitschätzung (docs/04 §8): verbleibende Spiele × Ø-Dauer / aktive Felder.
 * Ø-Dauer startet beim konfigurierten Wert und wird aus echten Zeiten nachgeführt.
 */
export function estimatePhaseEnd(t: Tournament, phaseId: Id, now: number): Eta {
  const comp = t.competitions.find((c) => c.phases.some((p) => p.id === phaseId));
  const phase = comp?.phases.find((p) => p.id === phaseId);
  if (!comp || !phase) return { remainingMatches: 0, runningMatches: 0, activeCourts: 0, avgMatchMinutes: 0, spreadMinutes: 0 };
  const rules = effectiveRules(comp.ruleSet, phase.ruleSetOverride);
  const avg = comp.measuredMatchMinutes ?? rules.plannedMatchMinutes;
  const remaining = phase.matches.filter((m) => m.status === 'open' || m.status === 'ready').length;
  const running = phase.matches.filter((m) => m.status === 'running');
  const courts = t.courts.filter((c) => c.active && (!phase.config.courtGroup || c.group === phase.config.courtGroup)).length;
  // Laufende Spiele: Restzeit aus Startzeit schätzen
  let runningRest = 0;
  for (const m of running) runningRest += Math.max(0, avg - (now - (m.startedAt ?? now)) / 60_000);
  const changeover = 2;
  const totalMinutes = courts > 0 ? (remaining * (avg + changeover) + runningRest) / courts : 0;
  const eta: Eta = {
    remainingMatches: remaining,
    runningMatches: running.length,
    activeCourts: courts,
    avgMatchMinutes: avg,
    spreadMinutes: Math.round(Math.sqrt(Math.max(1, remaining)) * avg * 0.25),
  };
  if (courts > 0) eta.endsAt = now + totalMinutes * 60_000;
  return eta;
}

/** Gleitender Mittelwert der Spieldauer nach einem beendeten Spiel */
export function updateMeasuredMinutes(previous: number | undefined, planned: number, durationMinutes: number): number {
  if (durationMinutes <= 0 || durationMinutes > planned * 4) return previous ?? planned;
  if (previous === undefined) return (planned + durationMinutes) / 2;
  return previous * 0.7 + durationMinutes * 0.3;
}
